import {
  ArrowBottomLeftIcon,
  ArrowBottomRightIcon,
  ArrowTopLeftIcon,
  ArrowTopRightIcon,
  AvatarIcon,
  BackpackIcon,
  BadgeIcon,
  BookmarkIcon,
  BoxModelIcon,
  ButtonIcon,
  CaretDownIcon,
  CaretSortIcon,
  CaretUpIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardIcon,
  ColumnSpacingIcon,
  Component1Icon,
  CornerBottomLeftIcon,
  CornerBottomRightIcon,
  CornerTopLeftIcon,
  CornerTopRightIcon,
  Cross2Icon,
  CrossCircledIcon,
  Crosshair2Icon,
  CursorArrowIcon,
  DashboardIcon,
  DotFilledIcon,
  DotIcon,
  DotsVerticalIcon,
  DownloadIcon,
  DrawingPinFilledIcon,
  DrawingPinIcon,
  ExclamationTriangleIcon,
  ExitIcon,
  ExternalLinkIcon,
  EyeOpenIcon,
  FileIcon,
  GearIcon,
  GridIcon,
  GroupIcon,
  HamburgerMenuIcon,
  HandIcon,
  ImageIcon,
  InfoCircledIcon,
  LightningBoltIcon,
  Link1Icon,
  Link2Icon,
  LinkNone2Icon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MinusCircledIcon,
  MixIcon,
  MoveIcon,
  Pencil2Icon,
  PersonIcon,
  PinBottomIcon,
  PinLeftIcon,
  PinRightIcon,
  PinTopIcon,
  PlusCircledIcon,
  PlusIcon,
  QuestionMarkCircledIcon,
  ReaderIcon,
  ReloadIcon,
  RowSpacingIcon,
  SewingPinIcon,
  Share1Icon,
  StretchHorizontallyIcon,
  TokensIcon,
  TrashIcon,
  UpdateIcon,
  UploadIcon,
} from '@radix-ui/react-icons'
import { type IconProps as RadixIconProps } from '@radix-ui/react-icons/dist/types'
import { Box } from '@radix-ui/themes'

import {
  forwardRef,
  type ForwardRefExoticComponent,
  type RefAttributes,
} from 'react'

type IconProps = RadixIconProps & { className?: string }
type IconComponentType = ForwardRefExoticComponent<
  IconProps & RefAttributes<SVGSVGElement>
>

const withIconProps = (IconComponent: IconComponentType, size?: number) => {
  const WrappedIcon = forwardRef<SVGSVGElement, IconProps>(
    ({ className, style, ...props }, ref) => (
      <IconComponent
        ref={ref}
        className={className}
        style={style}
        {...(size ? { width: size, height: size } : {})}
        {...props}
      />
    ),
  )

  WrappedIcon.displayName = `Wrapped(${IconComponent.displayName || IconComponent.name})`

  return WrappedIcon
}

export default withIconProps

export const DexIconDelete = withIconProps(TrashIcon)
export const DexIconFocusToElement = withIconProps(Crosshair2Icon)
export const DexIconFitToView = withIconProps(BoxModelIcon)
export const DexIconPlaceElement = withIconProps(SewingPinIcon)
export const DexIconClear = withIconProps(Cross2Icon)
export const DexIconClose = withIconProps(Cross2Icon)
export const DexIconInfo = withIconProps(InfoCircledIcon)
export const DexIconAdd = withIconProps(PlusIcon)
export const DexIconEdit = withIconProps(Pencil2Icon)
export const DexIconBack = withIconProps(ChevronLeftIcon)
export const DexIconNext = withIconProps(ChevronRightIcon)
export const DexIconImport = withIconProps(DownloadIcon)
export const DexIconExport = withIconProps(UploadIcon)
export const DexIconContextMenu = withIconProps(HamburgerMenuIcon)
export const DexIconMore = withIconProps(DotsVerticalIcon)
export const DexIconMatrix = withIconProps(GridIcon)
export const DexIconSend = withIconProps(ChevronRightIcon)
export const DexIconBreadcrumbArrow = withIconProps(ChevronRightIcon)
export const DexIconAscending = withIconProps(CaretUpIcon)
export const DexIconDescending = withIconProps(CaretDownIcon)
export const DexIconSort = withIconProps(CaretSortIcon)
export const DexIconWarning = withIconProps(ExclamationTriangleIcon)
export const DexIconSearch = withIconProps(MagnifyingGlassIcon)
export const DexIconBookmark = withIconProps(BookmarkIcon)
export const DexIconAttributeDefinitions = withIconProps(GearIcon)
export const DexIconPattern = withIconProps(MixIcon)
export const DexIconMerge = withIconProps(Share1Icon)
export const DexIconReverse = forwardRef<SVGSVGElement, IconProps>(
  ({ className, style, ...props }, ref) => (
    <svg
      ref={ref}
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      {...props}
    >
      {/* Arrow pointing right (top) */}
      <path
        d="M1 5h10.5M9 2.5L11.5 5 9 7.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Arrow pointing left (bottom) */}
      <path
        d="M14 10H3.5M6 7.5L3.5 10 6 12.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
)
DexIconReverse.displayName = 'DexIconReverse'
export const DexIconProject = withIconProps(BackpackIcon)
export const DexIconUser = withIconProps(PersonIcon)
export const DexIconPassword = withIconProps(LockClosedIcon)
export const DexIconLock = withIconProps(LockClosedIcon)
export const DexIconSelect = withIconProps(CursorArrowIcon)
export const DexIconSelectMultiple = withIconProps(GroupIcon)
export const DexIconRefresh = withIconProps(UpdateIcon)
export const DexIconReload = withIconProps(ReloadIcon)
export const DexIconOpen = withIconProps(FileIcon)
export const DexIconAccordion = withIconProps(ChevronDownIcon)
export const DexIconSettings = withIconProps(GearIcon)
export const DexIconList = withIconProps(ListBulletIcon)
export const DexIconGrid = withIconProps(DashboardIcon)
export const DexIconHelp = withIconProps(QuestionMarkCircledIcon)
export const DexIconStage = withIconProps(HandIcon)
export const DexIconUnstage = withIconProps(CrossCircledIcon)
export const DexIconPasteFromClipboard = withIconProps(ClipboardIcon)
export const DexIconPatternLayout = withIconProps(TokensIcon)
export const DexIconWidth = withIconProps(StretchHorizontallyIcon)
export const DexIconVisible = withIconProps(EyeOpenIcon)
export const DexIconZoomIn = withIconProps(PlusCircledIcon)
export const DexIconZoomOut = withIconProps(MinusCircledIcon)
export const DexIconPin = withIconProps(DrawingPinIcon)
export const DexIconPinned = withIconProps(DrawingPinFilledIcon)
export const DexIconToolbarLeft = withIconProps(PinLeftIcon)
export const DexIconToolbarTop = withIconProps(PinTopIcon)
export {
  DexAlignHCenterIcon as DexIconAlignHorizontalCenter,
  DexAlignVCenterIcon as DexIconAlignVerticalCenter,
} from './alignment-icons'
export const DexIconAlignHorizontalLeft = withIconProps(PinLeftIcon, 20)
export const DexIconAlignHorizontalRight = withIconProps(PinRightIcon, 20)
export const DexIconAlignVerticalTop = withIconProps(PinTopIcon, 20)
export const DexIconAlignVerticalBottom = withIconProps(PinBottomIcon, 20)
export const DexIconOk = withIconProps(CheckIcon)
export const DexIconHorizontalSpacing = withIconProps(ButtonIcon)
export const DexIconVerticalSpacing = ({ className = '' }) => (
  <div className={className}>
    <ButtonIcon
      style={{
        rotate: '90deg',
      }}
    />
  </div>
)
export const DexIconRowSpacing = withIconProps(RowSpacingIcon)
export const DexIconColumnSpacing = withIconProps(ColumnSpacingIcon)
export const DexIconYes = withIconProps(CheckIcon)
export const DexIconNo = withIconProps(Cross2Icon)
export const DexIconLink = withIconProps(Link2Icon)
export const DexIconLinkBroken = withIconProps(LinkNone2Icon)
export const DexIconChain = withIconProps(Link1Icon)
export const DexIconGenerate = withIconProps(ReloadIcon)
export const DexIconGroup = withIconProps(Component1Icon)
export const DexIconLegend = withIconProps(ReaderIcon)
export const DexIconReader = withIconProps(ReaderIcon)
export const DexIconReleaseNotes = withIconProps(LightningBoltIcon)
export const DexIconExternalLink = withIconProps(ExternalLinkIcon)
export const DexIconAvatar = withIconProps(AvatarIcon)
export const DexIconExit = withIconProps(ExitIcon)
export const DexIconAsset = withIconProps(BadgeIcon)
export const DexIconElement = withIconProps(DotFilledIcon)
export const DexIconDependency = withIconProps(ArrowBottomRightIcon)

/**
 * Base component for dependency direction icons
 * @param direction - 'from' shows filled dot at source (top-left), 'to' shows filled dot at target (bottom-right)
 */
const DexIconAddDependencyBase = forwardRef<
  SVGSVGElement,
  IconProps & { direction?: 'from' | 'to' | 'both' }
>(({ className, style, direction = 'both', ...props }, ref) => {
  const SourceDot =
    direction === 'from'
      ? DotFilledIcon
      : direction === 'to'
        ? DotIcon
        : DotFilledIcon
  const TargetDot =
    direction === 'from'
      ? DotIcon
      : direction === 'to'
        ? DotFilledIcon
        : DotFilledIcon

  return (
    <Box
      className={className}
      style={{
        ...style,
        width: 16,
        height: 16,
        position: 'relative',
        display: 'inline-block',
      }}
    >
      {/* source (top-left) */}
      <SourceDot
        ref={ref}
        {...props}
        style={{
          position: 'absolute',
          left: -6,
          top: -6,
          width: 18,
          height: 18,
        }}
      />

      {/* target (bottom-right) */}
      <TargetDot
        style={{
          position: 'absolute',
          right: -7,
          bottom: -7,
          width: 18,
          height: 18,
        }}
      />

      {/* arrow */}
      <ArrowBottomRightIcon
        style={{
          position: 'absolute',
          left: 3,
          top: 3,
          width: 10,
          height: 10,
        }}
      />
    </Box>
  )
})
DexIconAddDependencyBase.displayName = 'DexIconAddDependencyBase'

export const DexIconAddDependency = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <DexIconAddDependencyBase ref={ref} {...props} direction="both" />
  ),
)
DexIconAddDependency.displayName = 'DexIconAddDependency'

export const DexIconAddDependencyFrom = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <DexIconAddDependencyBase ref={ref} {...props} direction="from" />
  ),
)
DexIconAddDependencyFrom.displayName = 'DexIconAddDependencyFrom'

export const DexIconAddDependencyTo = forwardRef<SVGSVGElement, IconProps>(
  (props, ref) => (
    <DexIconAddDependencyBase ref={ref} {...props} direction="to" />
  ),
)
DexIconAddDependencyTo.displayName = 'DexIconAddDependencyTo'
// TODO: Refactor the code to support the icon props when needed
export const DexIconMoveElement = ({ className = '' }) => (
  <div className={className}>
    <Box className={'relative'}>
      <MoveIcon />
      <DotIcon className="absolute top-0" />
    </Box>
  </div>
)

export const DexIconAutoPlaceElement = ({ className = '' }) => (
  <div className={className}>
    <Box className={'relative'}>
      <MoveIcon />
      <DotIcon className="absolute top-0" />
      <span className="absolute -left-[2px] -top-[4px] text-[9px] font-bold leading-none">
        A
      </span>
    </Box>
  </div>
)

export const DexIconUsers = withIconProps(PersonIcon)

export const DexIconWorkspace = withIconProps(DashboardIcon)
export const DexIconImage = withIconProps(ImageIcon)

export const DexIconSnapTopLeft = () => (
  <Box
    style={{
      width: '1em',
      height: '1em',
      position: 'relative',
      display: 'inline-block',
    }}
  >
    <CornerTopLeftIcon
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '60%',
        height: '60%',
      }}
    />
    <ArrowTopLeftIcon
      style={{
        position: 'absolute',
        right: 0,
        bottom: 0,
        width: '66%',
        height: '66%',
      }}
    />
  </Box>
)

export const DexIconSnapTopRight = () => (
  <Box
    style={{
      width: '1em',
      height: '1em',
      position: 'relative',
      display: 'inline-block',
    }}
  >
    <CornerTopRightIcon
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        width: '60%',
        height: '60%',
      }}
    />
    <ArrowTopRightIcon
      style={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        width: '66%',
        height: '66%',
      }}
    />
  </Box>
)

export const DexIconSnapBottomLeft = () => (
  <Box
    style={{
      width: '1em',
      height: '1em',
      position: 'relative',
      display: 'inline-block',
    }}
  >
    <CornerBottomLeftIcon
      style={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        width: '60%',
        height: '60%',
      }}
    />
    <ArrowBottomLeftIcon
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        width: '66%',
        height: '66%',
      }}
    />
  </Box>
)

export const DexIconSnapBottomRight = () => (
  <Box
    style={{
      width: '1em',
      height: '1em',
      position: 'relative',
      display: 'inline-block',
    }}
  >
    <CornerBottomRightIcon
      style={{
        position: 'absolute',
        right: 0,
        bottom: 0,
        width: '60%',
        height: '60%',
      }}
    />
    <ArrowBottomRightIcon
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '66%',
        height: '66%',
      }}
    />
  </Box>
)
